import React, {useCallback} from 'react';
import {
    Popover,
    Flex,
    Icon,
    Text,
    PriorityIcon,
    PopoverPlacement,
    Button,
    Separator,
    Title
} from '@priority-software/priority-style-react';
import styles from './styles.module.scss';
import texts from '../../texts';
import {MoreInfoSuggestion} from '../../MoreInfoTrigger';
import {getTopDialogZindex} from '../../../../_shared/GWTBridge/invoke/priModalDlg';
import {startNewCollectionPeriod, rejectMoreInfoSuggestion} from '../../../Personalization/actions';
import {isRTL} from '../../../../Localization';
import {sendMonitor} from '../../../../utils/monitoring';
import {createDataTestId} from '../../../../utils/dataTestId';

type Props = {
    open: boolean;
    suggestion: MoreInfoSuggestion;
    anchor: HTMLElement;
    onApprove: () => void;
    closeDialog: () => void;
};

export const RecommendationDialog = ({open, suggestion, anchor, onApprove, closeDialog}: Props) => {
    const {form} = suggestion.indexedForm;
    const {zoomFname} = suggestion;

    const onClose = useCallback(() => {
        startNewCollectionPeriod();
        closeDialog();
    }, [closeDialog]);

    const handleSnooze = () => {
        sendMonitor('PersonalizationMoreDetailsPRLater');
        onClose();
    };

    const handleApprove = () => {
        sendMonitor('PersonalizationMoreDetailsPROK');
        onApprove();
    };

    const onReject = useCallback(() => {
        sendMonitor('PersonalizationMoreDetailsPRNO');
        rejectMoreInfoSuggestion(zoomFname, form.entityId);
        onClose();
    }, [zoomFname, form, onClose]);

    const DialogHeader = (
        <Flex
            justifyContent="flex-start"
            {...createDataTestId('popover-recommendation-dialog-title', true)}
        >
            <Icon componentClasses={styles.icon} icon={PriorityIcon.bulb} iconColor="primary-700" />
            <Title skin="h5" textColor="primary-700" text={texts.recommendation_title} />
        </Flex>
    );

    const renderSuggestionText = () => {
        const suggestionParts = texts.recommendation_suggestion.split('X');
        suggestionParts.splice(1, 0, form.formTitle);
        return (
            <Text skin="paragraph-2" componentClasses={styles.suggestion}>
                {suggestionParts.map((part, idx) => {
                    return idx === 1 ? <b key={idx}>{part}</b> : <span key={idx}>{part}</span>;
                })}
            </Text>
        );
    };

    const footer = (
        <Flex justifyContent="space-between">
            <Flex justifyContent="flex-start">
                <Button onClick={handleSnooze} skin="text">
                    {texts.snooze_btn}
                </Button>
                <Separator componentClasses={styles.separator} align="horizontal" height={35} />
                <Button onClick={onReject} skin="text">
                    {texts.reject_btn}
                </Button>
            </Flex>
            <Button size="medium" onClick={handleApprove}>
                {texts.ok_label}
            </Button>
        </Flex>
    );

    return (
        <Popover
            {...createDataTestId('popover-recommendation-dialog-container', true)}
            show={open}
            title={DialogHeader}
            content={
                <div {...createDataTestId('popover-recommendation-dialog-text', false)}>
                    {renderSuggestionText()}
                    {footer}
                </div>
            }
            anchor={anchor}
            zIndex={getTopDialogZindex()}
            placement={isRTL() ? PopoverPlacement.rightStart : PopoverPlacement.leftStart}
            rtl
        />
    );
};
